import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { ThemeContext } from 'styled-components';

import { ReactComponent as NotSupportedIcon } from '@pkg/assets/icons/not-supported.svg';
import { Typography } from '@pkg/components/DesignSystem';
import { AssetHandler } from '@pkg/components/Sections/Groups/GroupCard/NFTAsset/components/AssetHandler';
import YatLoader from '@pkg/components/Sections/Profile/Loader';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { toggleDisplaySnackbar } from '@app/store/actions/nft-manager';
import { type AppThunkPromise } from '@app/store/deferred-redux-store';
import { type NFTItem } from '@app/store/types/nft-manager';
import { type NFTContent } from '@app/store/types/yat-page-actions';

import {
    AddButton,
    DisabledMessage,
    ErrorIcon,
    ErrorIconWrapper,
    ErrorSquare,
    ErrorText,
    ErrorWrapper,
    NftAssetWrapper,
    NotSupportedOverlay,
    NotSupportedPill,
    NotSupportedText,
    PlayButtonWrapper,
    QuickAddWrapper,
    StyledDraggable,
    StyledPlayButton,
} from './styles';

export type AddItemHandler = (item: NFTItem, liquidIDFace?: number) => null | AppThunkPromise<unknown>;
export type RemoveItemHandler = (item: NFTItem) => null | AppThunkPromise<unknown>;

export type NftAssetProps = {
    item: NFTItem;
    disabled: boolean;
    liquidIDSelected: boolean;
    editingLiquidID: boolean;
    error?: boolean;
    preloading?: boolean;
    dragged?: boolean;
    onError(value: boolean): void;
    handleAddItem: AddItemHandler;
    handleRemoveItem: RemoveItemHandler;
};

const LOCALE_PATH = 'yat_nft_manager_';
const DRAGGABLE_ENABLED = false;

export function NftAsset({
    item,
    preloading,
    disabled,
    liquidIDSelected,
    editingLiquidID,
    dragged,
    handleAddItem,
    handleRemoveItem,
    error = false,
    onError,
}: NftAssetProps) {
    const { t: translate } = useTranslation(['yat_nft_manager']);

    const { ref: wrapperRef, inView } = useInView({ threshold: 0, triggerOnce: true });

    const theme = useContext(ThemeContext);
    const dispatch = useAppDispatch();

    const draggingItems = useAppSelector((state) => state.nftManager.draggingItems);
    const selectedAllItems = useAppSelector((state) => state.nftManager.selectedAllItems);
    const selectedItems = useAppSelector((state) => state.nftManager.selectedItems);

    const videoRef = useRef<HTMLVideoElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);

    const [hoverDragIcon, setHoverDragIcon] = useState(false);
    const [aspectRatio, setAspectRatio] = useState<number | undefined>();
    const [itemContent, setItemContent] = useState<NFTContent>();
    const [hovering, setHovering] = useState(false);
    const [canPlay, setCanPlay] = useState(false);

    useEffect(() => {
        if (item && item.content) {
            setItemContent(item.content);
        }
    }, [item]);

    const dragging = draggingItems.some((_item) => _item == item.id);

    const handleAspectRatio = (width: number, height: number) => {
        setAspectRatio(height / width);
    };

    const handleError = useCallback(() => {
        onError?.(true);
    }, [onError]);

    const handleLoaded = useCallback(() => {
        if (videoRef.current) {
            setCanPlay(true);
            return handleAspectRatio(videoRef.current.videoWidth, videoRef.current.videoHeight);
        }

        if (imageRef.current) {
            return handleAspectRatio(imageRef.current.naturalWidth, imageRef.current.naturalHeight);
        }
    }, []);

    // TODO consider hook or helper for this
    const handlePlay = useCallback(() => {
        const video = videoRef.current;
        if (video && canPlay) {
            const playPromise = video.play();
            if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                        return video.play();
                    })
                    .catch((error) => {
                        // eslint-disable-next-line no-console
                        console.warn(error);
                        return video.pause();
                    });
            }
        }
    }, [canPlay]);

    useLayoutEffect(() => {
        const video = videoRef?.current;

        if (video) {
            const isPlaying = Boolean(
                video &&
                    video.currentTime > 0 &&
                    !video.paused &&
                    !video.ended &&
                    video.readyState > video.HAVE_CURRENT_DATA,
            );

            setCanPlay(!isPlaying);
        }
    }, []);

    const onMouseEnter = () => {
        if (error) return;
        if (videoRef.current) handlePlay();
        setHovering(true);
    };

    const onMouseLeave = () => {
        if (error) return;
        if (videoRef.current) videoRef.current.pause();
        setHovering(false);
    };

    const addSingleItem = () => {
        dispatch(handleAddItem(item));
    };

    const removeSingleItem = () => {
        dispatch(handleRemoveItem(item));
    };

    const isVideo = Boolean(
        itemContent?.mime_type === 'video/mp4' && (itemContent?.video || itemContent?.animation_url),
    );

    // TODO: @andykenward Temporary fix to hide missing assets on error.
    useEffect(() => {
        if (
            (item.content?.image == null || item.content.image.length === 0) &&
            (item.content?.video == null || item.content.video.length === 0) &&
            !error
        ) {
            onError(true);
        }
    }, [error, item, onError]);

    const hasErrorOrDisabled = error || disabled;

    const showQuickAddButton =
        !(hasErrorOrDisabled && editingLiquidID) &&
        hovering &&
        !dragging &&
        !selectedItems.length &&
        !hoverDragIcon &&
        !selectedAllItems;

    const asset = useMemo(() => {
        if (!itemContent) return;
        return (
            <AssetHandler
                autoPlay={false}
                controls={false}
                imageUrl={itemContent.image}
                videoUrl={itemContent.video}
                fallback={itemContent.fallbackAssetUrl}
                onError={handleError}
                onLoad={handleLoaded}
                videoRef={videoRef}
                imageRef={imageRef}
            />
        );
    }, [itemContent, handleError, handleLoaded]);

    return (
        <NftAssetWrapper
            ref={wrapperRef}
            $hasBackground={preloading || !aspectRatio || aspectRatio !== 1 || dragging}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={() => {
                if (disabled && editingLiquidID) {
                    dispatch(toggleDisplaySnackbar(true, true));
                }
            }}
        >
            <YatLoader show={aspectRatio === undefined || preloading} />

            {inView && (
                <>
                    <StyledDraggable
                        // TODO: Dragging has been disabled since current implementation only adds elements to the top
                        // behaving the same was as quick add
                        allowDragging={!!aspectRatio && !liquidIDSelected && !preloading && DRAGGABLE_ENABLED}
                        id={item.id}
                        dragged={dragged}
                        $disabled={disabled}
                        $hidden={dragging && !dragged}
                        $loaded={!!aspectRatio && !preloading}
                        $hasSquareAspectRatio={aspectRatio === 1}
                        onHover={setHoverDragIcon}
                    >
                        {error ? (
                            <ErrorWrapper>
                                <ErrorSquare>
                                    <ErrorIconWrapper>
                                        <ErrorIcon />
                                    </ErrorIconWrapper>
                                </ErrorSquare>
                                <ErrorText>{translate(LOCALE_PATH + 'asset_error')}</ErrorText>
                            </ErrorWrapper>
                        ) : (
                            asset
                        )}
                    </StyledDraggable>

                    <AnimatePresence>
                        {isVideo && aspectRatio && !preloading && !dragging && !hovering && !hoverDragIcon && (
                            <PlayButtonWrapper
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: theme.transitions.speeds.normal }}
                            >
                                <StyledPlayButton />
                            </PlayButtonWrapper>
                        )}
                    </AnimatePresence>

                    <AnimatePresence>
                        {disabled && !editingLiquidID && (
                            <DisabledMessage>
                                <Typography weight="700" size={10} type="label">
                                    {translate('yat_nft_manager_micro_menu_added_to_grid')}
                                </Typography>
                            </DisabledMessage>
                        )}

                        {disabled && editingLiquidID && (
                            <NotSupportedOverlay>
                                <NotSupportedPill>
                                    <NotSupportedIcon />
                                    <NotSupportedText>{translate('yat_nft_manager_not_supported')}</NotSupportedText>
                                </NotSupportedPill>
                            </NotSupportedOverlay>
                        )}

                        {showQuickAddButton && (
                            <QuickAddWrapper
                                key="quick-add-wrapper"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: theme.transitions.speeds.normal }}
                            >
                                <AddButton
                                    color="white"
                                    size="small"
                                    variant="solid"
                                    onClick={(ev) => {
                                        ev.stopPropagation();
                                        hasErrorOrDisabled || liquidIDSelected ? removeSingleItem() : addSingleItem();
                                    }}
                                >
                                    {editingLiquidID
                                        ? liquidIDSelected
                                            ? translate(LOCALE_PATH + 'liquid_id_remove')
                                            : translate(LOCALE_PATH + 'liquid_id_add')
                                        : hasErrorOrDisabled
                                          ? translate(LOCALE_PATH + 'micro_menu_quick_unpublish')
                                          : translate(LOCALE_PATH + 'micro_menu_quick_add')}
                                </AddButton>
                            </QuickAddWrapper>
                        )}
                    </AnimatePresence>
                </>
            )}
        </NftAssetWrapper>
    );
}
